import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  COLOR_BACKGROUND_1,
  COLOR_ERROR_1,
  COLOR_ERROR_1_HOVER,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SUCCESS_1,
  COLOR_TERTIARY_1_100,
} from 'src/utils/colors';
import { callApi } from 'src/core/callApi';

const Kpi = ({ adminPassword }) => {
  const [affiliatePartnerEmailID, setAffiliatePartnerEmailID] = useState<string>('');
  const [affiliatePartnerName, setAffiliatePartnerName] = useState<string>('');
  const [affiliatePartnerKey, setAffiliatePartnerKey] = useState<string>('');
  const [showEmailOrNameMissingAlert, setShowEmailOrNameMissingAlert] = useState<boolean>(false);
  const [showAffiliateKeyLoader, setShowAffiliateKeyLoader] = useState<boolean>(false);
  const [promocodeName, setPromocodeName] = useState<string>('');
  const [promocodeDiscount, setPromocodeDiscount] = useState<string>('');
  const [promocodeFrequency, setPromocodeFrequency] = useState<string>('forever');
  const [promocodeOperation, setPromocodeOperation] = useState<string>('create');
  const [showDetailsMissingAlertInCouponActivation, setShowDetailsMissingAlertInCoupon] = useState<
    boolean
  >(false);
  const [couponApiSuccessResponse, setCouponApiSuccessResponse] = useState<string>('');
  const [isCouponActivationLoading, setCouponActivationLoading] = useState<boolean>(false);
  const [couponCodeError, setCouponCodeError] = useState<string>('');

  useEffect(() => {
    setCouponApiSuccessResponse('');
    setCouponCodeError('');
  }, [promocodeOperation]);

  const fetchApi = async (url, queryParams, retry = 0) => {
    if (retry > 5) return null;
    try {
      const res = await callApi(url, { queryParams }, false);
      const resString = JSON.stringify(res);
      if (resString == '{}') {
        throw new Error('Timeout');
      }
      return res;
    } catch (err) {
      console.log('Error: ', err);
      return fetchApi(url, queryParams, retry + 1);
    }
  };

  const handleAffiliatePartnerAccountCreation = async () => {
    setShowAffiliateKeyLoader(true);
    setShowEmailOrNameMissingAlert(false);
    const url =
      'https://a8q9o0b2r3.execute-api.us-east-2.amazonaws.com/default/createAffiliatePartner';
    const config = {
      queryParams: {
        admin_password: encodeURIComponent(adminPassword),
        name: affiliatePartnerName,
        email: encodeURIComponent(affiliatePartnerEmailID),
      },
    };
    if (affiliatePartnerName && affiliatePartnerEmailID) {
      const res = await callApi(url, config, false);
      setAffiliatePartnerKey(res.partnerKey);
      setShowEmailOrNameMissingAlert(false);
      setShowAffiliateKeyLoader(false);
    } else {
      setShowEmailOrNameMissingAlert(true);
      setShowAffiliateKeyLoader(false);
    }
  };

  const handleCouponActivationAndDeletion = async () => {
    setCouponActivationLoading(true);
    setCouponApiSuccessResponse('');
    setCouponCodeError('');
    if (
      (promocodeOperation == 'create' &&
        !promocodeDiscount &&
        !promocodeName &&
        !promocodeFrequency) ||
      (promocodeOperation == 'delete' && !promocodeName)
    ) {
      setShowDetailsMissingAlertInCoupon(true);
      setCouponActivationLoading(false);
    } else {
      setShowDetailsMissingAlertInCoupon(false);
      const url =
        'https://61psikryqf.execute-api.us-east-2.amazonaws.com/default/activateCouponCode';
      const couponActivationConfig = {
        queryParams: {
          admin_password: encodeURIComponent(adminPassword),
          discountedPer: promocodeDiscount,
          duration: encodeURIComponent(promocodeFrequency),
          couponName: encodeURIComponent(promocodeName),
          // isTest: true,
          operation: 'create',
        },
      };
      const couponDeletionConfig = {
        queryParams: {
          admin_password: encodeURIComponent(adminPassword),
          couponName: encodeURIComponent(promocodeName),
          // isTest: true,
          operation: 'delete',
        },
      };
      let res;
      if (promocodeOperation == 'create') {
        res = await callApi(url, couponActivationConfig, false);
      } else {
        res = await callApi(url, couponDeletionConfig, false);
      }

      if (res.message) {
        setCouponApiSuccessResponse(res.message);
        setCouponActivationLoading(false);
        setCouponCodeError('');
      }
      if (res.error) {
        setCouponApiSuccessResponse('');
        setCouponActivationLoading(false);
        setCouponCodeError(res.error);
      }
    }
  };

  return (
    <Box
      sx={{
        height: '95%',
        width: '100%',
        margin: '10px',
        overflowY: 'scroll',
      }}
    >
      <Box sx={{ display: 'flex', gap: 5, flexDirection: 'column', height: '100%' }}>
        {/* MARKETING KPI */}
        <Box>
          {/* MARKETING */}
          <Box sx={{ textAlign: 'center', margin: '10px' }}>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: '600' }}>
              Marketing KPIs
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 5, padding: '10px' }}>
            {/* Affiliate Partner Onboarding */}
            <Box sx={{ height: '550px', width: '450px', backgroundColor: '#121212' }}>
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" sx={{ color: '#ffff' }}>
                  Onboard Affiliate Partners
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    gap: 2,
                  }}
                >
                  <Typography>Name:</Typography>
                  <TextField
                    id="outlined-basic"
                    label=""
                    variant="outlined"
                    size="small"
                    value={affiliatePartnerName}
                    onChange={e => {
                      setAffiliatePartnerName(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff',
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    gap: 2,
                  }}
                >
                  <Typography>Email:</Typography>
                  <TextField
                    id="outlined-basic"
                    label=""
                    variant="outlined"
                    size="small"
                    value={affiliatePartnerEmailID}
                    onChange={e => {
                      setAffiliatePartnerEmailID(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff',
                      },
                    }}
                  />
                </Box>
                <Button
                  sx={{
                    backgroundColor: COLOR_PRIMARY_1,
                    cursor: 'pointer',
                    color: '#ffff',
                    '&:hover': { backgroundColor: COLOR_PRIMARY_2 },
                  }}
                  onClick={handleAffiliatePartnerAccountCreation}
                >
                  Create Affiliate Account
                </Button>

                {showEmailOrNameMissingAlert && (
                  <Alert severity="error">Please enter name and email Id.</Alert>
                )}

                {affiliatePartnerKey && (
                  <Alert severity="success">Generated Key is {affiliatePartnerKey}</Alert>
                )}

                {showAffiliateKeyLoader && <CircularProgress />}
              </Box>
            </Box>

            {/* Activate Promo code */}
            <Box sx={{ height: '550px', width: '450px', backgroundColor: '#121212' }}>
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <Button
                    sx={{ backgroundColor: COLOR_SUCCESS_1 }}
                    onClick={() => setPromocodeOperation('create')}
                  >
                    <Typography variant="body1" sx={{ color: '#ffff', borderRadius: '10px' }}>
                      Activate Promo Code
                    </Typography>
                  </Button>
                  <Button
                    sx={{ backgroundColor: COLOR_ERROR_1 }}
                    onClick={() => setPromocodeOperation('delete')}
                  >
                    <Typography variant="body1" sx={{ color: '#ffff', borderRadius: '10px' }}>
                      Delete Promo Code
                    </Typography>
                  </Button>
                </Box>

                {promocodeOperation == 'create' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        gap: 2,
                      }}
                    >
                      <Typography>Coupon Name:</Typography>
                      <TextField
                        id="outlined-basic"
                        label=""
                        variant="outlined"
                        size="small"
                        value={promocodeName}
                        onChange={e => {
                          setPromocodeName(e.target.value);
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        gap: 2,
                      }}
                    >
                      <Typography>% Discount:</Typography>
                      <TextField
                        id="outlined-basic"
                        label=""
                        variant="outlined"
                        size="small"
                        value={promocodeDiscount}
                        onChange={e => {
                          setPromocodeDiscount(e.target.value);
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        gap: 2,
                      }}
                    >
                      <Typography>Frequency:</Typography>
                      <FormControl>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={promocodeFrequency}
                          label=""
                          onChange={e => {
                            setPromocodeFrequency(e.target.value);
                          }}
                          sx={{
                            '& .MuiSelect-select': {
                              padding: '4px 0px 4px 10px',
                            },
                            marginRight: '10px',
                            backgroundColor: COLOR_BACKGROUND_1,
                          }}
                        >
                          <MenuItem value={'forever'}>Forever</MenuItem>
                          <MenuItem value={'once'}>Once</MenuItem>
                          <MenuItem value={'3_Months'}>First 3 Months</MenuItem>
                          <MenuItem value={'6_Months'}>First 6 Months</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Button
                      sx={{
                        backgroundColor: COLOR_PRIMARY_1,
                        cursor: 'pointer',
                        color: '#ffff',
                        '&:hover': { backgroundColor: COLOR_PRIMARY_2 },
                      }}
                      onClick={handleCouponActivationAndDeletion}
                    >
                      Activate Coupon
                    </Button>
                  </Box>
                )}
                {promocodeOperation == 'delete' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        gap: 2,
                      }}
                    >
                      <Typography>Coupon Name:</Typography>
                      <TextField
                        id="outlined-basic"
                        label=""
                        variant="outlined"
                        size="small"
                        value={promocodeName}
                        onChange={e => {
                          setPromocodeName(e.target.value);
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                          },
                        }}
                      />
                    </Box>
                    <Button
                      sx={{
                        backgroundColor: COLOR_ERROR_1_HOVER,
                        cursor: 'pointer',
                        color: '#ffff',
                        '&:hover': { backgroundColor: COLOR_ERROR_1_HOVER },
                      }}
                      onClick={handleCouponActivationAndDeletion}
                    >
                      Delete Coupon
                    </Button>
                  </Box>
                )}

                {showDetailsMissingAlertInCouponActivation && (
                  <Alert severity="error">Please enter all the details</Alert>
                )}

                {couponApiSuccessResponse && (
                  <Alert severity="success">{couponApiSuccessResponse}</Alert>
                )}

                {couponCodeError && <Alert severity="error">{couponCodeError}</Alert>}

                {isCouponActivationLoading && <CircularProgress />}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Usefull Resources */}
        <Box>
          <Box sx={{ textAlign: 'center', margin: '10px' }}>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: '600' }}>
              Usefull Resources
            </Typography>
          </Box>
          <Box
            sx={{
              width: '95%',
              // backgroundColor: '#121212',
              padding: '20px',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px' }}>
              <Typography variant="h6" sx={{ color: 'white' }}>
                See All KPIs At Airbtics :
              </Typography>
              <Button
                onClick={() => {
                  window.open(
                    'https://www.notion.so/airbtics/KPIs-be29b7cc247648e9ac51fe11175bc181',
                    '_blank'
                  );
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Airbtics KPIs
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px' }}>
              <Typography variant="h6" sx={{ color: 'white' }}>
                See Survey Results :
              </Typography>
              <Button
                onClick={() => {
                  window.open(
                    'https://insights.hotjar.com/sites/1926311/surveys/responses/968108?sort_by=-index',
                    '_blank'
                  );
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Survey Results
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px' }}>
              <Typography variant="h6" sx={{ color: 'white' }}>
                Stripe Dashboard :
              </Typography>
              <Button
                onClick={() => {
                  window.open('https://dashboard.stripe.com/dashboard', '_blank');
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Stripe
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px' }}>
              <Typography variant="h6" sx={{ color: 'white' }}>
                Google Analytics Dashboard :
              </Typography>
              <Button
                onClick={() => {
                  window.open(
                    'https://analytics.google.com/analytics/web/#/p379520711/reports/reportinghub?params=_u..nav%3Dmaui',
                    '_blank'
                  );
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Stripe
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Kpi;
